/**
 * API functions for Account Closure module.
 *
 * @taskId T464, T466
 * @see functional/Specs/specs_cycle_4.md HU-15-FE, HU-16-FE
 */

import { authFetch, normalizeApiError } from '@flexibilitypay/shared';
import type {
  CloseAccountInvoluntaryPayload,
  CloseAccountResponse,
  ClosureStatusResponse,
} from '../features/closure/types/closure.types';

const BASE_PATH = '/api/v1/accounts';

/**
 * Error específico de la API de cierre de cuentas.
 */
export class ClosureApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ClosureApiError';
  }
}

/**
 * POST /api/v1/accounts/{id}/close — Iniciar cierre involuntario de cuenta.
 *
 * @rule RULE-16 — Motivo obligatorio
 * @rule RULE-29 — Solo PSP_ADMIN
 * @see HU-50-BE
 */
export async function closeAccountInvoluntary(
  accountId: string,
  payload: CloseAccountInvoluntaryPayload,
): Promise<CloseAccountResponse> {
  const res = await authFetch(`${BASE_PATH}/${accountId}/close`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      type: 'INVOLUNTARY',
    }),
  });

  if (!res.ok) {
    const error = await normalizeApiError(res);
    throw new ClosureApiError(
      error.message ?? 'Error al iniciar cierre de cuenta',
      error.code ?? 'CLOSURE_ERROR',
      res.status,
    );
  }

  return res.json();
}

/**
 * GET /api/v1/accounts/{id}/closure-status — Consultar estado de cierre.
 *
 * @returns null si no hay proceso de cierre (404)
 * @see HU-51-BE
 */
export async function getClosureStatus(accountId: string): Promise<ClosureStatusResponse | null> {
  const res = await authFetch(`${BASE_PATH}/${accountId}/closure-status`);

  // 404 significa que no hay proceso de cierre activo — no es error
  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    const error = await normalizeApiError(res);
    throw new ClosureApiError(
      error.message ?? 'Error al consultar estado de cierre',
      error.code ?? 'CLOSURE_STATUS_ERROR',
      res.status,
    );
  }

  return res.json();
}
